// src/page/SummaryUsage.jsx
import React, { useEffect, useRef, useState, useMemo } from 'react';
import useAuthGuard from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../config';
import Select from 'react-select';

export default function SummaryUsage() {
    useAuthGuard();

    // === spot & ticket selects (live UI inputs) ===
    const [spotId, setSpotId] = useState('');
    const [spots, setSpots] = useState([]);
    const [loadingSpots, setLoadingSpots] = useState(false);
    const spotOptions = useMemo(
        () => spots
            .map(s => ({
                value: s.spotId ?? s.id ?? s.spot_id ?? '',
                label: s.spotName ?? s.name ?? s.spot_name ?? ''
            }))
            .filter(o => o.value && o.label),
        [spots]
    );

    const [ticketId, setTicketId] = useState('');
    const [tickets, setTickets] = useState([]);
    const [loadingTickets, setLoadingTickets] = useState(false);
    const ticketOptions = useMemo(
        () => tickets
            .map(t => ({ value: t.packageId, label: t.packageName }))
            .filter(o => o.value && o.label),
        [tickets]
    );

    useEffect(() => {
        const ac = new AbortController();
        (async () => {
            try {
                setLoadingSpots(true);
                const token = localStorage.getItem('access_token') || '';
                const res = await fetch(`${API_BASE_URL}/v1/spot?page=1&size=200`, {
                    headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                    signal: ac.signal
                });
                const data = await res.json().catch(() => ({}));
                const list = Array.isArray(data.items) ? data.items : (Array.isArray(data) ? data : []);
                setSpots(list);
            } catch {
                setSpots([]);
            } finally {
                setLoadingSpots(false);
            }
        })();
        return () => ac.abort();
    }, []);

    useEffect(() => {
        if (!spotId) { setTickets([]); setTicketId(''); return; }
        const ac = new AbortController();
        (async () => {
            try {
                setLoadingTickets(true);
                const token = localStorage.getItem('access_token') || '';
                const url = new URL(`${API_BASE_URL}/v1/ticket`);
                url.searchParams.set('size', '200');
                url.searchParams.set('sort', 'title');
                url.searchParams.set('dir', 'asc');
                url.searchParams.set('spotid', spotId);
                const res = await fetch(url.toString(), {
                    headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                    signal: ac.signal
                });
                const data = await res.json().catch(() => ({}));
                setTickets(Array.isArray(data.items) ? data.items : []);
            } catch {
                setTickets([]);
            } finally {
                setLoadingTickets(false);
            }
        })();
        return () => ac.abort();
    }, [spotId]);

    // === flatpickr (two ranges: acquired & used) ===
    const acqRef = useRef(null);
    const usedRef = useRef(null);
    // acquired (取得)
    const [fromAcq, setFromAcq] = useState('');
    const [toAcq, setToAcq] = useState('');
    // used (利用)
    const [fromUsed, setFromUsed] = useState('');
    const [toUsed, setToUsed] = useState('');
    const pad2 = (n) => String(n).padStart(2, '0');
    const fmtYMD = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
    const fmtDisp = (d) => `${d.getFullYear()}/${pad2(d.getMonth() + 1)}/${pad2(d.getDate())}`;
    const acqText = fromAcq && toAcq ? `${fmtDisp(fromAcq)} ~ ${fmtDisp(toAcq)}` : '';
    const usedText = fromUsed && toUsed ? `${fmtDisp(fromUsed)} ~ ${fmtDisp(toUsed)}` : '';

    // flatpickr: acquired
    useEffect(() => {
        if (!window.flatpickr) return;
        const ja = window.flatpickr.l10ns?.ja || {};
        const el = document.getElementById('summary_period_range_acq');
        if (!el) return;
        const fp = window.flatpickr(el, {
            mode: 'range',
            dateFormat: 'Y/m/d',
            locale: { ...ja, rangeSeparator: ' ~ ' },
            onChange: (dates) => {
                if (dates?.length === 2) {
                    setFromAcq(dates[0]);
                    setToAcq(dates[1]);
                } else if (!dates?.length) {
                    setFromAcq('');
                    setToAcq('');
                }
            }
        });
        acqRef.current = fp;
        return () => { try { fp.destroy(); } catch { } };
    }, []);
    // flatpickr: used
    useEffect(() => {
        if (!window.flatpickr) return;
        const ja = window.flatpickr.l10ns?.ja || {};
        const el = document.getElementById('summary_period_range_used');
        if (!el) return;
        const fp = window.flatpickr(el, {
            mode: 'range',
            dateFormat: 'Y/m/d',
            locale: { ...ja, rangeSeparator: ' ~ ' },
            onChange: (dates) => {
                if (dates?.length === 2) {
                    setFromUsed(dates[0]);
                    setToUsed(dates[1]);
                } else if (!dates?.length) {
                    setFromUsed('');
                    setToUsed('');
                }
            }
        });
        usedRef.current = fp;
        return () => { try { fp.destroy(); } catch { } };
    }, []);

    // === committed query states (used for API) ===
    const [qSpotId, setQSpotId] = useState('');
    const [qTicketId, setQTicketId] = useState('');
    // acquired
    const [qFromAcq, setQFromAcq] = useState(null);
    const [qToAcq, setQToAcq] = useState(null);
    // used
    const [qFromUsed, setQFromUsed] = useState(null);
    const [qToUsed, setQToUsed] = useState(null);
    const [qStatus, setQStatus] = useState(''); // "1,2,3" etc.
    // UI checkboxes (1,2,3,4,5,6,9) ※0=購入開始はUIに出していない
    const [statusChecks, setStatusChecks] = useState({
        1: false, // 購入済み
        2: false, // 利用中
        3: false, // 利用済み
        4: false, // 期限切れ
        5: false, // キャンセル(利用済み)
        6: false, // キャンセル(未使用)
        9: false, // 取消
    });
    // === list (API) ===
    const [rows, setRows] = useState([]);
    const [listLoading, setListLoading] = useState(false);
    const [listError, setListError] = useState('');

    // paging
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [total, setTotal] = useState(0);
    const pageCount = Math.max(1, Math.ceil((total || 0) / pageSize));
    const first = total === 0 ? 0 : (page - 1) * pageSize + 1;
    const last = Math.min(total, page * pageSize);
    const pages = useMemo(() => Array.from({ length: pageCount }, (_, i) => i + 1), [pageCount]);
    const goPrev = () => setPage(p => Math.max(1, p - 1));
    const goNext = () => setPage(p => Math.min(pageCount, p + 1));

    // sort
    const [sort, setSort] = useState({ key: 'used', dir: 'desc' }); // spot|package|voucheruse|user|used
    const toggleSort = (key) => setSort(prev => (
        prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }
    ));

    // formatters
    const fmtDateTime = (iso) => {
        if (!iso) return '';
        const d = new Date(iso);
        if (isNaN(d)) return '';
        return `${d.getFullYear()}/${pad2(d.getMonth() + 1)}/${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
    };
    const fmtInt = (v) => (v == null ? '' : String(v));

    // fetch list (only when committed states change OR page/sort changes)
    useEffect(() => {
        const ac = new AbortController();
        (async () => {
            try {
                setListLoading(true);
                setListError('');
                const token = localStorage.getItem('access_token') || '';
                const url = new URL(`${API_BASE_URL}/v1/summary/usage`);
                url.searchParams.set('page', String(page));
                url.searchParams.set('size', String(pageSize));
                url.searchParams.set('sort', sort.key);
                url.searchParams.set('dir', sort.dir);
                if (qSpotId) url.searchParams.set('spotid', qSpotId);
                if (qTicketId) url.searchParams.set('packageid', qTicketId);
                // acquired period (LIST expects YYYY-MM-DD)
                if (qFromAcq && qToAcq) {
                    url.searchParams.set('fromacq', fmtYMD(qFromAcq));
                    url.searchParams.set('toacq', fmtYMD(qToAcq));
                }
                // used period (LIST expects YYYY-MM-DD)
                if (qFromUsed && qToUsed) {
                    url.searchParams.set('from', fmtYMD(qFromUsed));
                    url.searchParams.set('to', fmtYMD(qToUsed));
                }
                if (qStatus) url.searchParams.set('status', qStatus);

                const res = await fetch(url.toString(), {
                    headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                    signal: ac.signal
                });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const j = await res.json();
                setRows(Array.isArray(j.items) ? j.items : []);
                setTotal(Number(j.total) || 0);
                if (j.size && Number(j.size) !== pageSize) setPageSize(Number(j.size));
            } catch (e) {
                if (e?.name === 'AbortError') return;
                console.error(e);
                setRows([]);
                setListError('読み込みに失敗しました');
            } finally {
                setListLoading(false);
            }
        })();
        return () => ac.abort();
    }, [page, pageSize, sort, qSpotId, qTicketId, qFromAcq, qToAcq, qFromUsed, qToUsed, qStatus]);

    useEffect(() => {
        if (page > pageCount) setPage(pageCount);
        if (page < 1) setPage(1);
    }, [page, pageCount]);

    // submit & clear
    const handleSubmit = (e) => {
        e.preventDefault();
        setQSpotId(spotId || '');
        setQTicketId(ticketId || '');
        // commit acquired period (取得)
        setQFromAcq(fromAcq || null);
        setQToAcq(toAcq || null);
        // commit used period (利用)
        setQFromUsed(fromUsed || null);
        setQToUsed(toUsed || null);
        const selected = Object.entries(statusChecks)
            .filter(([, v]) => v)
            .map(([k]) => k)
            .join(',');
        setQStatus(selected);
        setPage(1);
    };

    const handleClear = () => {
        setSpotId('');
        setTicketId('');
        setFromAcq('');
        setToAcq('');
        setFromUsed('');
        setToUsed('');
        setQSpotId('');
        setQTicketId('');
        setQFromAcq(null);
        setQToAcq(null);
        setQFromUsed(null);
        setQToUsed(null);
        setStatusChecks({ 1: false, 2: false, 3: false, 4: false, 5: false, 6: false, 9: false });
        setQStatus('');
        setPage(1);
    };

    // CSV export (uses committed states)
    const handleExportCsv = async () => {
        try {
            const token = localStorage.getItem('access_token') || '';
            const url = new URL(`${API_BASE_URL}/v1/summary/usage/export`);
            url.searchParams.set('sort', sort.key);
            url.searchParams.set('dir', sort.dir);
            if (qSpotId) url.searchParams.set('spotid', qSpotId);
            if (qTicketId) url.searchParams.set('packageid', qTicketId);
            // acquired (RFC3339)
            if (qFromAcq && qToAcq) {
                const s = new Date(qFromAcq.getFullYear(), qFromAcq.getMonth(), qFromAcq.getDate(), 0, 0, 0, 0);
                const e = new Date(qToAcq.getFullYear(), qToAcq.getMonth(), qToAcq.getDate(), 23, 59, 59, 999);
                url.searchParams.set('fromacq', s.toISOString());
                url.searchParams.set('toacq', e.toISOString());
            }
            // used (RFC3339)
            if (qFromUsed && qToUsed) {
                const s = new Date(qFromUsed.getFullYear(), qFromUsed.getMonth(), qFromUsed.getDate(), 0, 0, 0, 0);
                const e = new Date(qToUsed.getFullYear(), qToUsed.getMonth(), qToUsed.getDate(), 23, 59, 59, 999);
                url.searchParams.set('from', s.toISOString());
                url.searchParams.set('to', e.toISOString());
            }
            if (qStatus) url.searchParams.set('status', qStatus);

            const res = await fetch(url.toString(), {
                headers: { Accept: 'text/csv', ...(token ? { Authorization: `Bearer ${token}` } : {}) }
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);

            const blob = await res.blob();
            const objectUrl = window.URL.createObjectURL(blob);
            const d = new Date();
            const fname = `利用実績_${d.getFullYear()}${pad2(d.getMonth() + 1)}${pad2(d.getDate())}_${pad2(d.getHours())}${pad2(d.getMinutes())}.csv`;
            const a = document.createElement('a');
            a.href = objectUrl;
            a.download = fname;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(objectUrl);
        } catch (e) {
            console.error(e);
            alert('CSVのダウンロードに失敗しました');
        }
    };

    const statusLabel = (s) => {
        const m = {
            "0": "購入開始",
            "1": "購入済み",
            "2": "利用中",
            "3": "利用済み",
            "4": "期限切れ",
            "5": "キャンセル(利用済み)",
            "6": "キャンセル(未使用)",
            "9": "取消"
        };
        return m[String(s ?? '')] ?? '';
    };

    return (
        <Layout>
            <nav aria-label="breadcrumb" className="mb-3">
                <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item"><a href="/summary">実績管理</a></li>
                    <li className="breadcrumb-item active">利用実績</li>
                </ol>
            </nav>

            <div className="mb-9">
                <div className="row g-3 mb-4">
                    <div className="col-auto">
                        <h2 className="mb-0">利用実績</h2>
                    </div>
                </div>

                <div id="summary">
                    <div className="mb-4">
                        <form className="position-relative w-100" onSubmit={handleSubmit}>
                            <div className="mb-4">
                                <div className="d-flex flex-wrap gap-3 align-items-end">
                                    <div className="search-box" style={{ minWidth: 280 }}>
                                        <Select
                                            options={spotOptions}
                                            value={spotOptions.find(o => o.value === spotId) || null}
                                            onChange={(opt) => setSpotId(opt?.value || '')}
                                            isDisabled={loadingSpots}
                                            isClearable
                                            placeholder={loadingSpots ? '読み込み中…' : 'スポット選択'}
                                            classNamePrefix="react-select"
                                            className="w-100"
                                            menuPortalTarget={document.body}
                                            styles={{ container: (b) => ({ ...b, width: '100%', minWidth: 0 }), menuPortal: (b) => ({ ...b, zIndex: 9999 }) }}
                                            filterOption={(opt, input) => opt.label.toLowerCase().includes(input.toLowerCase())}
                                        />
                                    </div>
                                    <div className="search-box" style={{ minWidth: 280 }}>
                                        <Select
                                            options={ticketOptions}
                                            value={ticketOptions.find(o => o.value === ticketId) || null}
                                            onChange={(opt) => setTicketId(opt?.value || '')}
                                            isDisabled={!spotId || loadingTickets}
                                            isClearable
                                            placeholder={!spotId ? '' : (loadingTickets ? '読み込み中…' : '乗車券選択')}
                                            classNamePrefix="react-select"
                                            className="w-100"
                                            menuPortalTarget={document.body}
                                            styles={{ container: (b) => ({ ...b, width: '100%', minWidth: 0 }), menuPortal: (b) => ({ ...b, zIndex: 9999 }) }}
                                            filterOption={(opt, input) => opt.label.toLowerCase().includes(input.toLowerCase())}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="d-flex flex-wrap gap-3 align-items-end mt-3">
                                <div className="search-box">
                                    <label className="form-label mb-1 d-block">取得照会期間</label>
                                    <div className="position-relative">
                                        <input
                                            id="summary_period_range_acq"
                                            className="form-control search-input search pe-5"
                                            type="text"
                                            placeholder="YYYY/MM/DD ~ YYYY/MM/DD"
                                            value={acqText}
                                            readOnly
                                        />
                                        <span className="fas fa-calendar-alt search-box-icon position-absolute top-50 translate-middle-y" style={{ left: 12, pointerEvents: 'none' }} />
                                    </div>
                                </div>
                                <div className="search-box">
                                    <label className="form-label mb-1 d-block">利用照会期間</label>
                                    <div className="position-relative">
                                        <input
                                            id="summary_period_range_used"
                                            className="form-control search-input search pe-5"
                                            type="text"
                                            placeholder="YYYY/MM/DD ~ YYYY/MM/DD"
                                            value={usedText}
                                            readOnly
                                        />
                                        <span className="fas fa-calendar-alt search-box-icon position-absolute top-50 translate-middle-y" style={{ left: 12, pointerEvents: 'none' }} />
                                    </div>
                                </div>
                            </div>

                            <div className="d-flex flex-wrap gap-3 align-items-end mt-3">
                                <div>
                                    <label className="form-label mb-1 d-block">ステータス</label>
                                    <div className="d-flex flex-wrap gap-2">
                                        <div className="form-check">
                                            <input className="form-check-input" type="checkbox"
                                                checked={statusChecks[1]}
                                                onChange={(e) => setStatusChecks(s => ({ ...s, 1: e.target.checked }))} />
                                            <label className="form-check-label" htmlFor="status-withdrawn">購入済み</label>
                                        </div>
                                        <div className="form-check">
                                            <input className="form-check-input" type="checkbox"
                                                checked={statusChecks[2]}
                                                onChange={(e) => setStatusChecks(s => ({ ...s, 2: e.target.checked }))} />
                                            <label className="form-check-label" htmlFor="status-withdrawn">利用中</label>
                                        </div>
                                        <div className="form-check">
                                            <input className="form-check-input" type="checkbox"
                                                checked={statusChecks[3]}
                                                onChange={(e) => setStatusChecks(s => ({ ...s, 3: e.target.checked }))} />
                                            <label className="form-check-label" htmlFor="status-withdrawn">利用済み</label>
                                        </div>
                                        <div className="form-check">
                                            <input className="form-check-input" type="checkbox"
                                                checked={statusChecks[4]}
                                                onChange={(e) => setStatusChecks(s => ({ ...s, 4: e.target.checked }))} />
                                            <label className="form-check-label" htmlFor="status-withdrawn">期限切れ</label>
                                        </div>
                                        <div className="form-check">
                                            <input className="form-check-input" type="checkbox"
                                                checked={statusChecks[5]}
                                                onChange={(e) => setStatusChecks(s => ({ ...s, 5: e.target.checked }))} />
                                            <label className="form-check-label" htmlFor="status-withdrawn">キャンセル（利用済み）</label>
                                        </div>
                                        <div className="form-check">
                                            <input className="form-check-input" type="checkbox"
                                                checked={statusChecks[6]}
                                                onChange={(e) => setStatusChecks(s => ({ ...s, 6: e.target.checked }))} />
                                            <label className="form-check-label" htmlFor="status-withdrawn">キャンセル（未使用）</label>
                                        </div>
                                        <div className="form-check">
                                            <input className="form-check-input" type="checkbox"
                                                checked={statusChecks[9]}
                                                onChange={(e) => setStatusChecks(s => ({ ...s, 9: e.target.checked }))} />
                                            <label className="form-check-label" htmlFor="status-withdrawn">取り消し</label>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="d-flex gap-2 mt-3">
                                <button type="submit" className="btn btn-primary">検索</button>
                                <button type="button" className="btn btn-outline-secondary" onClick={handleClear}>クリア</button>

                                <div className="ms-xxl-auto d-flex gap-3">
                                    <button className="btn btn-link text-body me-4 px-0" type="button" onClick={handleExportCsv}>
                                        <span className="fa-solid fa-file-export fs-9 me-2"></span>CSVダウンロード
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>

                    <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-body-emphasis border-top border-bottom border-translucent position-relative top-1">
                        <div className="table-responsive scrollbar mx-n1 px-1">
                            <table className="table fs-9 mb-0">
                                <thead>
                                    <tr>
                                        <th className="align-middle ps-3 text-nowrap" role="button" onClick={() => { toggleSort('spot'); setPage(1); }}>
                                            スポット名<span className="fas ms-1 text-muted fa-sort" />
                                        </th>
                                        <th className="align-middle ps-3 text-nowrap" role="button" onClick={() => { toggleSort('package'); setPage(1); }}>
                                            乗車券名<span className="fas ms-1 text-muted fa-sort" />
                                        </th>
                                        <th className="align-middle ps-3 text-nowrap" role="button" onClick={() => { toggleSort('created'); setPage(1); }}>
                                            取得日時<span className="fas ms-1 text-muted fa-sort" />
                                        </th>
                                        <th className="align-middle ps-3 text-nowrap" role="button" onClick={() => { toggleSort('used'); setPage(1); }}>
                                            利用日時<span className="fas ms-1 text-muted fa-sort" />
                                        </th>
                                        <th className="align-middle ps-3 text-nowrap" role="button" onClick={() => { toggleSort('expire'); setPage(1); }}>
                                            有効期限<span className="fas ms-1 text-muted fa-sort" />
                                        </th>
                                        <th className="align-middle ps-3 text-nowrap" role="button" onClick={() => { toggleSort('status'); setPage(1); }}>
                                            ステータス<span className="fas ms-1 text-muted fa-sort" />
                                        </th>
                                        <th className="align-middle ps-3 text-nowrap">大人</th>
                                        <th className="align-middle ps-3 text-nowrap">小人</th>
                                        <th className="align-middle ps-3 text-nowrap" role="button" onClick={() => { toggleSort('user'); setPage(1); }}>
                                            会員番号<span className="fas ms-1 text-muted fa-sort" />
                                        </th>
                                        <th className="align-middle ps-3 text-nowrap">バウチャー利用ID</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {listError && (
                                        <tr><td className="ps-3 text-danger" colSpan={7}>{listError}</td></tr>
                                    )}
                                    {!listError && listLoading && (
                                        <tr><td className="ps-3 text-body-tertiary" colSpan={7}>読み込み中…</td></tr>
                                    )}
                                    {!listError && !listLoading && rows.length === 0 && (
                                        <tr><td className="ps-3 text-body-tertiary" colSpan={7}>データがありません</td></tr>
                                    )}
                                    {!listError && !listLoading && rows.map((r, i) => (
                                        <tr key={i}>
                                            <td className="ps-3">{r.spotName || ''}</td>
                                            <td className="ps-3">{r.packageName || ''}</td>
                                            <td className="ps-3">{fmtDateTime(r.createdAt)}</td>
                                            <td className="ps-3">{fmtDateTime(r.usedDate)}</td>
                                            <td className="ps-3">{fmtDateTime(r.effectivePeriodEndDate)}</td>
                                            <td className="ps-3">{statusLabel(r.tokenStatus)}</td>
                                            <td className="ps-3">{fmtInt(r.countAdult)}</td>
                                            <td className="ps-3">{fmtInt(r.countChild)}</td>
                                            <td className="ps-3">{r.userNumber || ''}</td>
                                            <td className="ps-3">{r.voucherUseId || ''}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        <div className="row align-items-center justify-content-between py-2 pe-0 fs-9">
                            <div className="col-auto d-flex align-items-center gap-3">
                                <p className="mb-0 d-none d-sm-block me-1 fw-semibold text-body">
                                    {total === 0 ? '0 items' : `${first} to ${last} items of ${total}`}
                                </p>
                            </div>

                            <div className="col-auto d-flex align-items-center">
                                <button type="button" className="page-link" onClick={goPrev} disabled={page <= 1}>
                                    <span className="fas fa-chevron-left" />
                                </button>

                                <ul className="mb-0 pagination">
                                    {pages.map(p => (
                                        <li key={p} className={`page-item ${p === page ? 'active' : ''}`}>
                                            <button type="button" className="page-link" onClick={() => setPage(p)}>
                                                {p}
                                            </button>
                                        </li>
                                    ))}
                                </ul>

                                <button type="button" className="page-link pe-0" onClick={goNext} disabled={page >= pageCount}>
                                    <span className="fas fa-chevron-right" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
}
